import { useState } from 'react'
import { useMyTags, useAvailableTags, useAssignTag, useRemoveTag } from '@/hooks/useTags'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { EmptyState } from '@/components/profile/EmptyState'
import { Tags, CheckCircle2, Plus, X } from 'lucide-react'

export function TagsPage() {
  const { data: myTags = [], isLoading } = useMyTags()
  const { data: allTags = [] } = useAvailableTags()
  const assignTag = useAssignTag()
  const removeTag = useRemoveTag()
  const [assigningTagId, setAssigningTagId] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<Record<string, string>>({})

  const assignedIds = new Set(myTags.map((rt) => rt.tag_id))
  const available = allTags.filter((t) => !assignedIds.has(t.id))
  const assigningTag = allTags.find((t) => t.id === assigningTagId)

  const handleAssign = () => {
    if (!assigningTagId) return
    assignTag.mutate({ tagId: assigningTagId, metadata }, { onSuccess: () => { setAssigningTagId(null); setMetadata({}) } })
  }

  if (isLoading) return <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />)}</div>

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">My Tags</CardTitle></CardHeader>
        <CardContent>
          {myTags.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tags assigned yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {myTags.map((rt) => (
                <span key={rt.id} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-white" style={{ backgroundColor: rt.tag?.display_color ?? '#6b7280' }}>
                  {rt.tag?.label}
                  {rt.is_verified && <CheckCircle2 className="h-3 w-3" />}
                  {!rt.is_verified && (
                    <button onClick={() => removeTag.mutate(rt.id)} className="ml-1 opacity-70 hover:opacity-100"><X className="h-3 w-3" /></button>
                  )}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Available Tags</CardTitle></CardHeader>
        <CardContent>
          {available.length === 0 ? (
            <EmptyState icon={Tags} title="All tags assigned" description="You've claimed all available tags." />
          ) : (
            <div className="space-y-2">
              {available.map((tag) => (
                <div key={tag.id} className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <span className="inline-block h-2.5 w-2.5 rounded-full mr-2" style={{ backgroundColor: tag.display_color ?? '#6b7280' }} />
                    <span className="text-sm font-medium">{tag.label}</span>
                    {tag.description && <p className="mt-0.5 text-xs text-muted-foreground">{tag.description}</p>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setAssigningTagId(tag.id); setMetadata({}) }}>
                    <Plus className="h-3 w-3" /> Request
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assignment dialog */}
      {assigningTag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAssigningTagId(null)}>
          <div className="w-full max-w-sm rounded-lg bg-background p-6 shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-1 font-semibold">{assigningTag.label}</h3>
            <p className="mb-4 text-sm text-muted-foreground">{assigningTag.description}</p>
            {(() => {
              const schema = assigningTag.metadata_schema as { required?: string[]; optional?: string[] } | null
              const fields = [...(schema?.required ?? []), ...(schema?.optional ?? [])]
              if (fields.length === 0) return null
              return (
                <div className="space-y-3 mb-4">
                  {fields.map((f) => (
                    <div key={f} className="space-y-1">
                      <Label className="capitalize">{f.replace(/_/g, ' ')}{schema?.required?.includes(f) ? ' *' : ''}</Label>
                      <Input value={metadata[f] ?? ''} onChange={(e) => setMetadata((m) => ({ ...m, [f]: e.target.value }))} />
                    </div>
                  ))}
                </div>
              )
            })()}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setAssigningTagId(null)}>Cancel</Button>
              <Button className="flex-1" onClick={handleAssign} disabled={assignTag.isPending}>Submit</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
